import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import { useAdminWebsites } from "hooks/ovizia/useAdminWebsites";
import { useWorkspaceIdentity } from "hooks/ovizia/useWorkspaceIdentity";
import { useWebsiteVersions } from "hooks/ovizia/useWebsiteVersions";
import { useWebsiteAuditHistory } from "hooks/ovizia/useOviziaAuditLogs";
import {
  getContent,
  regenerateWebsiteContent,
  archiveWebsite,
  resolveWebsiteUrl,
} from "hooks/ovizia/contentApi";
import { fetchWebsiteDomains } from "hooks/ovizia/domainService";

const Icon = ({ name, size = 14, style = {} }) => (
  <i
    className={`ti ti-${name}`}
    style={{ fontSize: size, lineHeight: 1, ...style }}
    aria-hidden="true"
  />
);

const C = {
  green: "#0e5604",
  greenBg: "#e7f6ef",
  border: "#e5e3dc",
  bgSurface: "#f7f6f2",
  text: "#1a1a1a",
  textMuted: "#6b6b6b",
  textFaint: "#a0a0a0",
  red: "#dc2626",
  redBg: "#fef2f2",
  amber: "#b45309",
  amberBg: "#fffbeb",
  blue: "#2563eb",
  blueBg: "#eff6ff",
};

function StatusBadge({ status }) {
  const map = {
    PUBLISHED: { bg: C.greenBg, fg: C.green, label: "Published" },
    DRAFT: { bg: C.bgSurface, fg: C.textMuted, label: "Draft" },
  };
  const s = map[status] || map.DRAFT;
  return (
    <span
      style={{
        background: s.bg,
        color: s.fg,
        fontSize: 11,
        fontWeight: 600,
        padding: "3px 8px",
        borderRadius: 999,
        width: "max-content",
      }}
    >
      {s.label}
    </span>
  );
}

function DomainStatusChip({ status }) {
  if (!status)
    return <span style={{ fontSize: 12, color: C.textFaint }}>No domain</span>;
  const map = {
    ACTIVE: { icon: "circle-check", color: C.green, label: "Active" },
    PENDING_DNS: { icon: "clock", color: C.amber, label: "Pending DNS" },
    PROVISIONING_ERROR: {
      icon: "circle-x",
      color: C.red,
      label: "Provisioning error",
    },
  };
  const s = map[status];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 12,
        color: s.color,
        fontWeight: 600,
      }}
    >
      <Icon name={s.icon} size={13} /> {s.label}
    </span>
  );
}

function SchemaFlag({ stale }) {
  if (!stale)
    return <span style={{ fontSize: 12, color: C.textFaint }}>Up to date</span>;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 12,
        color: C.amber,
        fontWeight: 600,
      }}
    >
      <Icon name="alert-triangle" size={13} /> Stale schema
    </span>
  );
}

function Tab({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "8px 14px",
        border: "none",
        borderBottom: active ? `2px solid ${C.green}` : "2px solid transparent",
        background: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: 600,
        color: active ? C.green : C.textMuted,
      }}
    >
      <Icon name={icon} size={14} /> {label}
    </button>
  );
}

function ConfirmBar({ label, onConfirm, onCancel, danger, confirming }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: danger ? C.redBg : C.blueBg,
        border: `1px solid ${
          danger ? "rgba(220,38,38,0.3)" : "rgba(37,99,235,0.25)"
        }`,
        borderRadius: 8,
        padding: "10px 14px",
        marginTop: 10,
      }}
    >
      <span
        style={{
          fontSize: 12,
          color: danger ? C.red : C.blue,
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={onCancel}
          disabled={confirming}
          style={{
            fontSize: 12,
            padding: "5px 10px",
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            background: "#fff",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={confirming}
          style={{
            fontSize: 12,
            padding: "5px 10px",
            border: "none",
            borderRadius: 6,
            background: danger ? C.red : C.blue,
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
            opacity: confirming ? 0.7 : 1,
          }}
        >
          {confirming ? "Working…" : "Confirm"}
        </button>
      </div>
    </div>
  );
}

// mirrors deriveDomainStatus in AdminWebsiteService (backend) so a mapping
// fetched fresh from fetchWebsiteDomains shows the same
// ACTIVE / PENDING_DNS / PROVISIONING_ERROR labels as the list view.
function deriveDomainStatus(mapping) {
  const failed =
    mapping.dnsStatus === "CONFIGURATION_FAILED" ||
    mapping.hostingStatus === "FAILED";
  if (failed) return "PROVISIONING_ERROR";

  const fullyActive =
    mapping.dnsStatus === "CONFIGURED" && mapping.hostingStatus === "ACTIVE";
  if (fullyActive) return "ACTIVE";

  return "PENDING_DNS";
}

function DetailPanel({ site, onClose }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [confirming, setConfirming] = useState(null);
  const [actionInFlight, setActionInFlight] = useState(false);

  const {
    entries: auditLog,
    loading: auditLoading,
    fetchHistory: fetchAuditLog,
  } = useWebsiteAuditHistory(site.id);

  const {
    workspace,
    ownerEmail,
    loading: identityLoading,
    fetchWorkspaceIdentity,
  } = useWorkspaceIdentity();

  const {
    versions,
    loading: versionsLoading,
    reverting,
    fetchVersions,
    revertToVersion,
  } = useWebsiteVersions(site.id);

  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(true);

  // getContent returns the WebsiteContent wrapper (id, websiteId, version,
  // etc.)
  const parsedContent = (() => {
    if (!content?.contentJson) return null;
    try {
      return JSON.parse(content.contentJson);
    } catch {
      return null;
    }
  })();

  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(true);

  useEffect(() => {
    fetchWorkspaceIdentity(site.workspaceId);
    fetchVersions();
    fetchAuditLog();

    setContentLoading(true);
    getContent(site.id)
      .then((data) => setContent(data))
      .finally(() => setContentLoading(false));

    setDomainsLoading(true);
    fetchWebsiteDomains(site.id)
      .then((data) => setDomains(Array.isArray(data) ? data : []))
      .catch(() => setDomains([]))
      .finally(() => setDomainsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site.id, site.workspaceId]);

  const handleRegenerate = async () => {
    setActionInFlight(true);
    try {
      await regenerateWebsiteContent(site.id);
      message.success("Content regenerated");
      const data = await getContent(site.id);
      setContent(data);
      fetchAuditLog();
    } catch (err) {
      message.error("Failed to regenerate content");
    } finally {
      setActionInFlight(false);
      setConfirming(null);
    }
  };

  const handleArchive = async () => {
    setActionInFlight(true);
    try {
      await archiveWebsite(site.id);
      message.success("Website archived");
      fetchAuditLog();
    } catch (err) {
      message.error("Failed to archive website");
    } finally {
      setActionInFlight(false);
      setConfirming(null);
    }
  };

  const handleRevert = async (versionId) => {
    try {
      await revertToVersion(versionId);
      fetchAuditLog();
    } catch {
      // useWebsiteVersions already surfaces the error toast
    }
  };


  const OVIZIA_APP_BASE_URL =
    process.env.REACT_APP_ENV === "dev"
      ? "https://dev.ovizia.com"
      : "https://ovizia.com";

  const handleOpenEditor = () => {
    const fragment = new URLSearchParams({
      authToken: sessionStorage.getItem("authToken") ?? "",
      refreshToken: sessionStorage.getItem("refreshToken") ?? "",
      accessTokenExpiresAt:
        sessionStorage.getItem("accessTokenExpiresAt") ?? "",
      refreshTokenExpiresAt:
        sessionStorage.getItem("refreshTokenExpiresAt") ?? "",
      userPrivileges: sessionStorage.getItem("userPrivileges") ?? "",
    });
    const targetPath = encodeURIComponent(`/admin/websites/${site.id}/editor`);
    const handoffUrl = `${OVIZIA_APP_BASE_URL}/auth/handoff?targetPath=${targetPath}&adminAccess=1#${fragment.toString()}`;
    window.open(handoffUrl, "_blank", "noopener,noreferrer");
  };

  const primaryDomain = domains.find((d) => d.primaryDomain) ?? domains[0];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        justifyContent: "flex-end",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          width: 640,
          maxWidth: "94vw",
          height: "100%",
          background: "#fff",
          boxShadow: "-8px 0 24px rgba(0,0,0,0.12)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* header */}
        <div
          style={{
            padding: "18px 22px",
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h2
                  style={{
                    fontSize: 17,
                    fontWeight: 800,
                    color: C.green,
                    margin: 0,
                  }}
                >
                  {site.name}
                </h2>
                <StatusBadge status={site.status} />
              </div>
              <p
                style={{ fontSize: 12, color: C.textMuted, margin: "4px 0 0" }}
              >
                {site.slug} · {site.template}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                color: C.textMuted,
                padding: 4,
              }}
            >
              <Icon name="x" size={18} />
            </button>
          </div>

          {/* owner strip */}
          <div
            style={{
              display: "flex",
              gap: 18,
              marginTop: 14,
              padding: "10px 12px",
              background: C.bgSurface,
              borderRadius: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="building" size={13} style={{ color: C.textFaint }} />
              <span style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>
                {identityLoading
                  ? "Loading…"
                  : workspace?.name ?? "Unknown workspace"}
              </span>
            </div>
            {/* <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="mail" size={13} style={{ color: C.textFaint }} />
              <span style={{ fontSize: 12, color: C.text }}>
                {identityLoading ? "Loading…" : ownerEmail ?? "No owner found"}
              </span>
            </div> */}
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: site.subscriptionLabel === "Pro" ? C.green : C.textFaint,
                marginLeft: "auto",
              }}
            >
              {site.subscriptionLabel}
            </span>
          </div>

          {/* tabs */}
          <div
            style={{
              display: "flex",
              gap: 4,
              marginTop: 14,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <Tab
              active={tab === "overview"}
              onClick={() => setTab("overview")}
              icon="world"
              label="Overview"
            />
            <Tab
              active={tab === "content"}
              onClick={() => setTab("content")}
              icon="code"
              label="Content"
            />
            <Tab
              active={tab === "domains"}
              onClick={() => setTab("domains")}
              icon="list-tree"
              label="Domains"
            />
            <Tab
              active={tab === "versions"}
              onClick={() => setTab("versions")}
              icon="history"
              label="Versions"
            />
            <Tab
              active={tab === "actions"}
              onClick={() => setTab("actions")}
              icon="tool"
              label="Actions"
            />
          </div>
        </div>

        {/* body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px" }}>
          {tab === "overview" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div
                style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "8px 12px",
                    background: C.bgSurface,
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  Live preview
                </div>
                <div
                  style={{
                    height: 220,
                    background: "#fafaf8",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <Icon name="world" size={28} style={{ color: C.textFaint }} />
                  {/* <span style={{ fontSize: 12, color: C.textFaint }}>
                    iframe preview renders here
                  </span> */}
                  <button
                    onClick={async () => {
                      const url = await resolveWebsiteUrl(
                        site,
                        site.workspaceId,
                      );
                      if (url)
                        window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.green,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Open live site <Icon name="external-link" size={12} />
                  </button>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.textFaint,
                      textTransform: "uppercase",
                    }}
                  >
                    Content schema
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <SchemaFlag stale={site.schemaStale} />
                  </div>
                </div>
                <div
                  style={{
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: C.textFaint,
                      textTransform: "uppercase",
                    }}
                  >
                    Domain
                  </div>
                  <div style={{ marginTop: 4 }}>
                    {domainsLoading ? (
                      <span style={{ fontSize: 12, color: C.textFaint }}>
                        Loading…
                      </span>
                    ) : (
                      <DomainStatusChip
                        status={
                          primaryDomain
                            ? deriveDomainStatus(primaryDomain)
                            : null
                        }
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* workspace details */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.textFaint,
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}
                >
                  Workspace details
                </div>
                {identityLoading ? (
                  <div style={{ fontSize: 12, color: C.textFaint }}>
                    Loading…
                  </div>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        padding: "10px 12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.textFaint,
                          textTransform: "uppercase",
                        }}
                      >
                        Account type
                      </div>
                      <div
                        style={{ fontSize: 13, color: C.text, marginTop: 2 }}
                      >
                        {workspace?.accountType ?? "—"}
                      </div>
                    </div>
                    <div
                      style={{
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        padding: "10px 12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.textFaint,
                          textTransform: "uppercase",
                        }}
                      >
                        Product
                      </div>
                      <div
                        style={{ fontSize: 13, color: C.text, marginTop: 2 }}
                      >
                        {workspace?.app ?? "—"}
                      </div>
                    </div>
                    <div
                      style={{
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        padding: "10px 12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.textFaint,
                          textTransform: "uppercase",
                        }}
                      >
                        Organization ID
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: C.text,
                          marginTop: 2,
                          fontFamily: "monospace",
                        }}
                      >
                        {workspace?.organizationId ?? "—"}
                      </div>
                    </div>
                    <div
                      style={{
                        border: `1px solid ${C.border}`,
                        borderRadius: 8,
                        padding: "10px 12px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: C.textFaint,
                          textTransform: "uppercase",
                        }}
                      >
                        Subscription ID
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: C.text,
                          marginTop: 2,
                          fontFamily: "monospace",
                        }}
                      >
                        {workspace?.subscriptionId ?? "—"}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* workspace members */}
              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.textFaint,
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}
                >
                  Workspace members
                </div>
                {identityLoading ? (
                  <div style={{ fontSize: 12, color: C.textFaint }}>
                    Loading…
                  </div>
                ) : !workspace?.users || workspace.users.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.textFaint }}>
                    No members found.
                  </div>
                ) : (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    {workspace.users.map((m) => (
                      <div
                        key={m.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          fontSize: 12,
                          padding: "8px 10px",
                          background: C.bgSurface,
                          borderRadius: 6,
                        }}
                      >
                        <div>
                          <span style={{ color: C.text, fontWeight: 600 }}>
                            {/* ASSUMPTION: UserDTO exposes `email` — adjust if named differently */}
                            {m.user?.email ?? "Unknown user"}
                          </span>
                          {m.roleNames?.length > 0 && (
                            <span style={{ color: C.textFaint, marginLeft: 6 }}>
                              · {[...m.roleNames].join(", ")}
                            </span>
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color:
                              m.membershipStatus === "ACTIVE"
                                ? C.green
                                : C.textFaint,
                            textTransform: "uppercase",
                          }}
                        >
                          {m.membershipStatus ?? "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.textFaint,
                    textTransform: "uppercase",
                    marginBottom: 8,
                  }}
                >
                  Recent activity
                </div>
                {auditLoading ? (
                  <div style={{ fontSize: 12, color: C.textFaint }}>
                    Loading…
                  </div>
                ) : auditLog.length === 0 ? (
                  <div style={{ fontSize: 12, color: C.textFaint }}>
                    No actions recorded for this website yet.
                  </div>
                ) : (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    {auditLog.map((entry, i) => {
                      const toneColor =
                        entry.tone === "positive"
                          ? C.green
                          : entry.tone === "negative"
                          ? C.red
                          : entry.tone === "warning"
                          ? C.amber
                          : C.textMuted;
                      return (
                        <div
                          key={entry.id ?? i}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            fontSize: 12,
                            padding: "7px 10px",
                            background: C.bgSurface,
                            borderRadius: 6,
                          }}
                        >
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              color: C.text,
                            }}
                          >
                            <Icon
                              name={entry.icon}
                              size={13}
                              style={{ color: toneColor }}
                            />
                            {entry.label}
                            <span style={{ color: C.textFaint }}>
                              by{" "}
                              {entry.actorEmail ??
                                entry.actorName ??
                                entry.actorId ??
                                "unknown"}
                            </span>
                          </span>
                          <span style={{ color: C.textFaint }}>
                            {entry.createdAt
                              ? new Date(entry.createdAt).toLocaleString()
                              : ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === "content" && (
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <span style={{ fontSize: 12, color: C.textMuted }}>
                  {content && !contentLoading
                    ? `v${content.version}${
                        content.active ? " · active" : ""
                      } — read-only, full editing lives in the admin editor`
                    : "Current active content (read-only — full editing lives in the admin editor)"}
                </span>
                <button
                  onClick={() =>
                    navigator.clipboard.writeText(
                      JSON.stringify(parsedContent, null, 2),
                    )
                  }
                  disabled={!parsedContent}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 12,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    padding: "5px 10px",
                    background: "#fff",
                    cursor: parsedContent ? "pointer" : "not-allowed",
                    opacity: parsedContent ? 1 : 0.5,
                  }}
                >
                  <Icon name="copy" size={12} /> Copy
                </button>
              </div>
              {contentLoading ? (
                <div style={{ fontSize: 12, color: C.textFaint, padding: 14 }}>
                  Loading content…
                </div>
              ) : !content ? (
                <div style={{ fontSize: 12, color: C.textFaint, padding: 14 }}>
                  No content found for this website.
                </div>
              ) : !parsedContent ? (
                <div style={{ fontSize: 12, color: C.red, padding: 14 }}>
                  Failed to parse contentJson — it may be malformed.
                </div>
              ) : (
                <pre
                  style={{
                    background: "#1a1a1a",
                    color: "#d4d4d4",
                    fontSize: 12,
                    lineHeight: 1.6,
                    padding: 14,
                    borderRadius: 8,
                    overflowX: "auto",
                    fontFamily: "monospace",
                    maxHeight: 620,
                  }}
                >
                  {JSON.stringify(parsedContent, null, 2)}
                </pre>
              )}
            </div>
          )}

          {tab === "domains" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {domainsLoading ? (
                <div style={{ fontSize: 12, color: C.textFaint, padding: 14 }}>
                  Loading domains…
                </div>
              ) : domains.length === 0 ? (
                <div
                  style={{
                    border: `1px solid ${C.border}`,
                    borderRadius: 10,
                    padding: "24px 16px",
                    textAlign: "center",
                    color: C.textFaint,
                    fontSize: 13,
                  }}
                >
                  No domain connected to this website.
                </div>
              ) : (
                domains.map((d) => {
                  const status = deriveDomainStatus(d);
                  return (
                    <div
                      key={d.id ?? d.domainName}
                      style={{
                        border: `1px solid ${C.border}`,
                        borderRadius: 10,
                        padding: "14px 16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: C.text,
                          }}
                        >
                          {d.domainName}{" "}
                          {d.primaryDomain && (
                            <span
                              style={{
                                fontSize: 10,
                                color: C.textFaint,
                                fontWeight: 600,
                              }}
                            >
                              · primary
                            </span>
                          )}
                        </span>
                        <DomainStatusChip status={status} />
                      </div>
                      {status === "PROVISIONING_ERROR" && (
                        <p
                          style={{
                            fontSize: 11,
                            color: C.red,
                            margin: "8px 0 0",
                          }}
                        >
                          {d.errorMessage ||
                            d.hostingErrorMessage ||
                            "Provisioning failed."}
                        </p>
                      )}
                    </div>
                  );
                })
              )}
              <p style={{ fontSize: 11, color: C.textFaint }}>
                Retry-provisioning and certificate-refresh actions aren't wired
                yet — pending a domain management endpoint.
              </p>
            </div>
          )}

          {tab === "versions" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {versionsLoading ? (
                <div style={{ fontSize: 12, color: C.textFaint, padding: 14 }}>
                  Loading versions…
                </div>
              ) : versions.length === 0 ? (
                <div style={{ fontSize: 12, color: C.textFaint, padding: 14 }}>
                  No saved versions yet.
                </div>
              ) : (
                versions.map((v, i) => (
                  <div
                    key={v.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      border: `1px solid ${C.border}`,
                      borderRadius: 8,
                      padding: "10px 12px",
                    }}
                  >
                    <div>
                      <div
                        style={{ fontSize: 13, fontWeight: 600, color: C.text }}
                      >
                        v{v.version ?? versions.length - i}{" "}
                        {v.active && "· current"}
                      </div>
                      <div style={{ fontSize: 11, color: C.textFaint }}>
                        {v.aiGenerated
                          ? "AI generated"
                          : "Visual edits auto-saved"}{" "}
                        · {new Date(v.updatedAt).toLocaleString()}
                      </div>
                    </div>
                    {!v.active && (
                      <button
                        onClick={() => handleRevert(v.id)}
                        disabled={reverting}
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          padding: "5px 10px",
                          borderRadius: 6,
                          border: `1px solid ${C.border}`,
                          background: "#fff",
                          cursor: "pointer",
                          opacity: reverting ? 0.6 : 1,
                        }}
                      >
                        {reverting ? "Reverting…" : "Revert"}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {tab === "actions" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div
                style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: "14px 16px",
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  Open in editor
                </div>
                <p
                  style={{
                    fontSize: 12,
                    color: C.textMuted,
                    margin: "4px 0 10px",
                  }}
                >
                  View and edit this website as if signed in as its owner.
                  Logged for this session.
                </p>
                <button
                  onClick={handleOpenEditor}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "7px 14px",
                    borderRadius: 6,
                    border: "none",
                    background: C.text,
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Open editor
                </button>
              </div>

              <div
                style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 10,
                  padding: "14px 16px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Icon name="refresh" size={14} style={{ color: C.amber }} />
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                    Regenerate content
                  </div>
                </div>
                <p
                  style={{
                    fontSize: 12,
                    color: C.textMuted,
                    margin: "4px 0 10px",
                  }}
                >
                  Resets sections to template defaults. Owner's manual edits
                  will be lost — use to fix a stale schema.
                </p>
                {confirming === "regenerate" ? (
                  <ConfirmBar
                    label="This will discard manual edits on this site."
                    onConfirm={handleRegenerate}
                    onCancel={() => setConfirming(null)}
                    danger
                    confirming={actionInFlight}
                  />
                ) : (
                  <button
                    onClick={() => setConfirming("regenerate")}
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "7px 14px",
                      borderRadius: 6,
                      border: `1px solid rgba(180,83,9,0.4)`,
                      background: C.amberBg,
                      color: C.amber,
                      cursor: "pointer",
                    }}
                  >
                    Regenerate
                  </button>
                )}
              </div>

              {/* <div
                style={{
                  border: `1px solid rgba(220,38,38,0.25)`,
                  borderRadius: 10,
                  padding: "14px 16px",
                  background: C.redBg,
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 700, color: C.red }}>
                  Archive website
                </div>
                <p
                  style={{
                    fontSize: 12,
                    color: C.textMuted,
                    margin: "4px 0 10px",
                  }}
                >
                  Unpublishes the site. Content is preserved and can be
                  restored.
                </p>
                {confirming === "archive" ? (
                  <ConfirmBar
                    label="Site will be unpublished immediately."
                    onConfirm={handleArchive}
                    onCancel={() => setConfirming(null)}
                    danger
                    confirming={actionInFlight}
                  />
                ) : (
                  <button
                    onClick={() => setConfirming("archive")}
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "7px 14px",
                      borderRadius: 6,
                      border: "1px solid rgba(220,38,38,0.4)",
                      background: "#fff",
                      color: C.red,
                      cursor: "pointer",
                    }}
                  >
                    Archive
                  </button>
                )}
              </div> */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const GRID_COLS = "1fr 1.2fr 1.2fr 0.8fr 0.8fr 1fr 24px";

export default function WebsiteAdminConsole() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [staleOnly, setStaleOnly] = useState(false);
  const [selected, setSelected] = useState(null);
  const { sites, totalElements, loading, fetchSites, fetchSiteById } =
    useAdminWebsites();

  // deep link from elsewhere (e.g. the activity timeline's Website chip):
  useEffect(() => {
    const deepLinkId = searchParams.get("websiteId");
    if (!deepLinkId) return;

    fetchSiteById(deepLinkId).then((site) => {
      if (site) setSelected(site);
    });

    // consume the param so a later manual search/close doesn't re-trigger this
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("websiteId");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (document.getElementById("__tabler_icons__")) return;
    const link = document.createElement("link");
    link.id = "__tabler_icons__";
    link.rel = "stylesheet";
    link.href =
      "https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      fetchSites({ query, staleOnly, page: 0, size: 20 });
    }, 300);
    return () => clearTimeout(handle);
  }, [query, staleOnly, fetchSites]);

  return (
    <div
      style={{
        fontFamily: "Inter, system-ui, sans-serif",
        background: "#fdfdfb",
        minHeight: "100vh",
        padding: "28px 32px",
      }}
    >
      <div style={{ margin: "0 auto" }}>
        <div style={{ marginBottom: 20 }}>
          <h1
            style={{ fontSize: 20, fontWeight: 800, color: C.green, margin: 0 }}
          >
            Website support console
          </h1>
          <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>
            Find a customer's website, see who owns it, and take action to help.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              padding: "9px 12px",
              background: "#fff",
            }}
          >
            <Icon name="search" size={15} style={{ color: C.textFaint }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by website name, slug, or domain..."
              style={{
                border: "none",
                outline: "none",
                flex: 1,
                fontSize: 13,
                fontFamily: "inherit",
              }}
            />
          </div>
          <button
            onClick={() => setStaleOnly((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              fontWeight: 600,
              padding: "0 14px",
              borderRadius: 8,
              cursor: "pointer",
              border: `1px solid ${staleOnly ? C.amber : C.border}`,
              background: staleOnly ? C.amberBg : "#fff",
              color: staleOnly ? C.amber : C.textMuted,
            }}
          >
            <Icon name="alert-triangle" size={13} /> Stale schema only
          </button>
        </div>

        <div
          style={{
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            background: "#fff",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: GRID_COLS,
              padding: "9px 16px",
              background: C.bgSurface,
              fontSize: 10,
              fontWeight: 700,
              color: C.textFaint,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            <span>Website</span>
            <span>Template</span>
            <span>Workspace</span>
            <span>Status</span>
            <span>Schema</span>
            <span>Domain</span>
            <span />
          </div>
          {loading ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                color: C.textFaint,
                fontSize: 13,
              }}
            >
              Loading websites...
            </div>
          ) : sites.length === 0 ? (
            <div
              style={{
                padding: 40,
                textAlign: "center",
                color: C.textFaint,
                fontSize: 13,
              }}
            >
              No websites match your search.
            </div>
          ) : (
            sites.map((site) => (
              <div
                key={site.id}
                onClick={() => setSelected(site)}
                style={{
                  display: "grid",
                  gridTemplateColumns: GRID_COLS,
                  padding: "12px 16px",
                  borderTop: `1px solid ${C.border}`,
                  cursor: "pointer",
                  alignItems: "center",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = C.bgSurface)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                    {site.name}
                  </div>
                  <div style={{ fontSize: 11, color: C.textFaint }}>
                    {site.slug}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: C.textFaint }}>
                  {site.template}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: C.textFaint,
                    fontFamily: "monospace",
                  }}
                >
                  {site.workspaceId ? `${site.workspaceId.slice(0, 15)}…` : "—"}
                </div>
                <StatusBadge status={site.status} />
                <SchemaFlag stale={site.schemaStale} />
                <DomainStatusChip status={site.domainStatus} />
                <Icon
                  name="chevron-right"
                  size={15}
                  style={{ color: C.textFaint }}
                />
              </div>
            ))
          )}
        </div>

        {!loading && sites.length > 0 && (
          <p style={{ fontSize: 11, color: C.textFaint, marginTop: 10 }}>
            Showing {sites.length} of {totalElements}
          </p>
        )}
      </div>

      {selected && (
        <DetailPanel site={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
